import React from 'react';

import { useLatestRef, useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { ReconnectWallet } from '@components/modals/ReconnectWallet';
import { useAuth } from '@context/auth/base';
import { type LoginSuccessValue, useConnectedAuth } from '@context/auth/connected';
import { useSecondaryModal } from '@context/secondaryModal';

export function useConnectedWallet<Return, Args extends readonly unknown[]>({
  onAuth,
  onClose,
}: {
  onAuth: (auth: LoginSuccessValue, ...args: Args) => Return;
  onClose?: () => void;
}) {
  const { openModal, closeCancelModal } = useSecondaryModal();
  const { authUser } = useAuth();
  const { loginRequireConnection, walletAddress, login, walletLogout } = useConnectedAuth();

  const latestOnAuth = useLatestRef(onAuth);
  const latestOnClose = useLatestRef(onClose);

  const handleSignin = useStableCallback(async (...args: Args) => {
    closeCancelModal();

    if (walletAddress && authUser && walletAddress === authUser.publicAddress) {
      return latestOnAuth.current(
        {
          authUser,
          connectedWalletAddress: walletAddress,
        },
        ...args,
      );
    }

    const auth = await loginRequireConnection();

    if (auth?.stop) {
      latestOnClose.current?.();
      /**
       * This logout could catch broken wallet states.
       * And here we can't control if the promise fails
       * and observability over it would just be noise
       */
      walletLogout().catch(() => null);
      return null;
    }

    return latestOnAuth.current(
      {
        authUser: auth.authUser,
        connectedWalletAddress: auth.connectedWalletAddress,
      },
      ...args,
    );
  });

  return useStableCallback((...args: Args) => {
    if (!authUser) {
      login();
      return null;
    }

    if (walletAddress && authUser.publicAddress === walletAddress) {
      return latestOnAuth.current(
        {
          authUser,
          connectedWalletAddress: walletAddress,
        },
        ...args,
      );
    }

    openModal('RECONNECT_WALLET', {
      body: <ReconnectWallet handleSignin={() => handleSignin(...args)} />,
    });

    return null;
  });
}
